import Form from "@rjsf/react-bootstrap";
import React, { FunctionComponent, useEffect } from "react";
import { JSONSchema7 } from "json-schema";
import { Link, useNavigate } from "react-router";
import { toast } from "react-toastify";
import firebase from "firebase/compat/app";
import validator from "@rjsf/validator-ajv8";
import { UiSchema } from "@rjsf/utils";
import { Container, Card, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";

import { PrimaryButton } from "../common/button/Buttons";
import { useAuthRegistration } from "../../hooks/useAuthRegistration";
import { getErrorByCode } from "../../helper/firebase/getErrorByCode";
import WebtieLogo from "../common/webtie-logo.png";

const schema: JSONSchema7 = {
  type: "object",
  required: ["email", "password", "passwordConfirmation"],
  properties: {
    email: {
      type: "string",
      title: "E-Mail"
    },
    password: {
      type: "string",
      title: "Passwort",
      minLength: 6
    },
    passwordConfirmation: {
      type: "string",
      title: "Passwort bestätigen",
      minLength: 6
    }
  }
};

const uiSchema: UiSchema = {
  email: {
    "ui:widget": "email"
  },
  password: {
    "ui:widget": "password"
  },
  passwordConfirmation: {
    "ui:widget": "password"
  }
};

function transformErrors(errors: any): any {
  return errors.map((error: any): any => {
    if (error.name === "minLength") {
      error.message = "Das Passwort sollte nicht kürzer als 6 Zeichen sein.";
    }
    return error;
  });
}

interface Props {
  user: firebase.User | null | undefined;
}

const SignUp: FunctionComponent<Props> = ({ user }: Props) => {
  const navigate = useNavigate();
  const { createUserWithEmailAndPassword, isLoading } = useAuthRegistration();

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const submitSignUp = async (formData: any): Promise<void> => {
    const { email, password, passwordConfirmation } = formData;

    if (!password === passwordConfirmation) {
      toast.error("Passwörter stimmen nicht überein.");
    }

    createUserWithEmailAndPassword(email, password).catch((e) => {
      toast.error(`Die Registrierung ist fehlgeschlagen. ${getErrorByCode(e.code)}`);
    });
  };

  return (
    <>
      <Helmet>
        <title>Spielgruppe Online - Registrieren</title>
        <meta name="description" content="Erstellen Sie ein Konto, um Ihre Spielgruppe-Anmeldungen zu verwalten" />
      </Helmet>
      <div
        className="min-vh-100 d-flex flex-column"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <Container className="flex-grow-1 d-flex align-items-center justify-content-center py-5">
          <Row className="w-100 justify-content-center">
            <Col md={6} lg={5} xl={4}>
              <Card className="shadow-lg border-0 rounded-lg">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <img
                      src={WebtieLogo}
                      alt="Webtie Logo"
                      className="img-fluid mb-3"
                      style={{ maxWidth: "200px", height: "auto" }}
                    />
                    <h2 className="text-dark mb-3" style={{ fontFamily: "Varela, sans-serif", fontWeight: "600" }}>
                      Konto erstellen
                    </h2>
                    <p className="text-muted mb-4">Registrieren Sie sich, um Anmeldungen zu verwalten</p>
                  </div>

                  {/* Registration Form */}
                  <Form
                    validator={validator}
                    schema={schema}
                    uiSchema={uiSchema}
                    onSubmit={(e) => submitSignUp(e.formData)}
                    showErrorList={false}
                    transformErrors={transformErrors}
                    className="signup-form">
                    <div className="mb-3">
                      <p className="small text-muted text-center">
                        Mit der Registrierung akzeptieren Sie die{" "}
                        <a target={"_blank"} href="/agb" className="text-decoration-none fw-bold">
                          AGBs
                        </a>
                      </p>
                    </div>
                    <div className="d-grid gap-2">
                      <PrimaryButton active={!isLoading} type="submit" size="lg">
                        Registrieren
                      </PrimaryButton>
                    </div>
                  </Form>

                  <div className="text-center mt-4">
                    <p className="text-muted">
                      Haben Sie bereits einen Account?{" "}
                      <Link to="/" className="text-decoration-none fw-bold">
                        Hier gehts zum Login
                      </Link>
                    </p>
                  </div>
                </Card.Body>
              </Card>

              <div className="text-center mt-4">
                <p className="text-white-50 small">Spielgruppe Online - Anmeldungen einfach verwalten</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default SignUp;
